import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, LoadingSpinner } from '../../components/atoms';
import { useSeriesActions } from '../../hooks/useSeries';
import { useCastActions, useCastAssignment } from '../../hooks/useCasts';
import type { DramaDetailResponse } from '../../types/api/drama';
import {
  //formatDateForDisplay,
  formatDateTimeForDisplay,
} from '../../types/api/drama';
import { resolveAssetUrl } from '../../utils/url';
import toast from 'react-hot-toast';
import { CastAssignmentModal } from '../../components/organisms/CastAssignmentModal';

/**
 * SeriesDetailPage Component
 *
 * Separate page for viewing series details.
 * Features:
 * - Display series information (title, description, structure)
 * - Show genres and primary genre
 * - Display thumbnail
 * - Show series metadata
 * - Navigation to edit page
 */
export const SeriesDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { seriesId } = useParams<{ seriesId: string }>();
  const { getSeries } = useSeriesActions();
  const { searchCasts } = useCastActions();

  const [seriesDetails, setSeriesDetails] = useState<DramaDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCastModalOpen, setIsCastModalOpen] = useState(false);

  const contentIdForCasts = seriesDetails?.drama?.dramaId ?? seriesId ?? '';
  const {
    assignCast,
    removeCast,
    updateRole,
    getCasts,
  } = useCastAssignment('drama', contentIdForCasts);

  // Load series data
  useEffect(() => {
    if (!seriesId) {
      toast.error('Series ID is required');
      navigate('/pages/series');
      return;
    }

    const loadSeries = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const details = await getSeries(seriesId);
        setSeriesDetails(details);
        setError(null);
      } catch (err: any) {
        console.error('Failed to load series:', err);
        setError(err.message || 'Failed to load series. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadSeries();
  }, [seriesId, navigate, getSeries]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!seriesDetails || !seriesDetails.drama) {
    return (
      <div className="text-center py-12">
        {error ? (
          <>
            <p className="text-red-600 mb-4">{error}</p>
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="primary"
                onClick={() => window.location.reload()}
                className="mt-4"
              >
                Retry
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/pages/series')}
                className="mt-4"
              >
                Back to Series
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-gray-600">Series not found</p>
            <Button
              variant="outline"
              onClick={() => navigate('/pages/series')}
              className="mt-4"
            >
              Back to Series
            </Button>
          </>
        )}
      </div>
    );
  }

  const drama = seriesDetails.drama;

  const handleFetchAssignedCasts = async () => {
    return await getCasts();
  };

  const handleSearchCasts = async (keyword: string) => {
    const page = 1; // backend now 1-based
    const size = 10;
    const pageData = await searchCasts(keyword, page, size);
    return pageData.items;
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="text-left">
          <h1 className="text-2xl font-bold text-gray-900">Series Details</h1>
          <p className="text-gray-600 mt-1">View comprehensive series information</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => setIsCastModalOpen(true)}
          >
            Manage Casts
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/pages/series')}
            leftIcon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            }
          >
            Back to Series
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate(`/pages/series/${drama.dramaId}/edit`)}
            leftIcon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            }
          >
            Edit Series
          </Button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Thumbnail Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-6">
            {/* Thumbnail */}
            <div className="relative mb-6">
              {drama.thumbnailUrl ? (
                <img
                  src={resolveAssetUrl(drama.thumbnailUrl)}
                  alt={drama.title}
                  className="w-full aspect-[2/3] rounded-xl border border-gray-300 object-cover shadow-md"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className="w-full aspect-[2/3] bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl border border-gray-300 flex items-center justify-center shadow-md"
                style={{ display: drama.thumbnailUrl ? 'none' : 'flex' }}
              >
                <div className="text-center">
                  <svg
                    className="w-20 h-20 text-gray-400 mx-auto mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-sm font-medium text-gray-500">No Thumbnail</p>
                </div>
              </div>
            </div>

            {/* Status Badges */}
            <div className="space-y-3">
              <div className="text-center">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Status
                </p>
                <span
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold ${
                    drama.status === 'ACTIVE'
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : drama.status === 'INACTIVE'
                      ? 'bg-gray-50 text-gray-700 border border-gray-200'
                      : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                  }`}
                >
                  {drama.status}
                </span>
              </div>
              <div className="text-center">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Access Level
                </p>
                <span
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold ${
                    drama.accessLevel === 'FREE'
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'bg-purple-50 text-purple-700 border border-purple-200'
                  }`}
                >
                  {drama.accessLevel}
                </span>
              </div>
              <div className="text-center">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Type
                </p>
                <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                  {drama.dramaType}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Series Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-left">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{drama.title}</h2>
            <p className="text-gray-600 leading-relaxed text-base">{drama.description}</p>
          </div>

          {/* Series Details Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center pb-4">
              <svg
                className="w-5 h-5 mr-2 text-[#EAB308]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Series Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-[#EAB308]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h10M4 18h6"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Structure
                  </p>
                  <p className="text-gray-900 font-medium">
                    {drama.totalSeasons} season{drama.totalSeasons !== 1 ? 's' : ''} •{' '}
                    {drama.totalEpisodes} episode{drama.totalEpisodes !== 1 ? 's' : ''} •{' '}
                    {drama.episodeDuration} sec
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Language
                  </p>
                  <p className="text-gray-900 font-medium">{drama.languageName}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Upload Date
                  </p>
                  <p className="text-gray-900 font-medium">
                    {formatDateTimeForDisplay(drama.uploadDate)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-indigo-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Priority
                  </p>
                  <p className="text-gray-900 font-medium">
                    {drama.priority ?? '—'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-yellow-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.21 3.72a1 1 0 00.95.69h3.915c.969 0 1.371 1.24.588 1.81l-3.166 2.302a1 1 0 00-.364 1.118l1.21 3.72c.3.921-.755 1.688-1.54 1.118l-3.166-2.302a1 1 0 00-1.175 0l-3.166 2.302c-.784.57-1.838-.197-1.539-1.118l1.21-3.72a1 1 0 00-.364-1.118L2.296 9.147c-.783-.57-.38-1.81.588-1.81h3.915a1 1 0 00.95-.69l1.21-3.72z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Rating
                  </p>
                  <p className="text-gray-900 font-medium">
                    {drama.ratingCount > 0
                      ? `${drama.averageRating.toFixed(1)} ★ (${drama.ratingCount})`
                      : 'Not rated'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    Content ID
                  </p>
                  <p className="text-gray-900 font-medium text-xs font-mono break-all">
                    {drama.contentId}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Genres Card */}
          {seriesDetails.genres && seriesDetails.genres.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center pb-4">
                <svg
                  className="w-5 h-5 mr-2 text-[#EAB308]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
                Genres
              </h3>
              <div className="flex flex-wrap gap-3">
                {seriesDetails.genres.map((genre) => (
                  <span
                    key={genre.genreId}
                    className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      genre.isPrimary
                        ? 'bg-[#EAB308] text-white shadow-sm'
                        : 'bg-gray-50 text-gray-800 border border-gray-200'
                    }`}
                    style={
                      genre.isPrimary
                        ? {}
                        : { borderColor: genre.colorCode || '#E5E7EB', borderWidth: '2px' }
                    }
                  >
                    {genre.name}
                    {genre.isPrimary && (
                      <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">
                        Primary
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Casts Card */}
          {seriesDetails.casts && seriesDetails.casts.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center pb-4">
                <svg
                  className="w-5 h-5 mr-2 text-[#EAB308]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a4 4 0 00-3-3.87M9 20h6m-3-4a4 4 0 01-4-4m4 4a4 4 0 004-4m-4 4v1m0-13a4 4 0 014 4m-4-4a4 4 0 00-4 4m4-4V3m0 18v-1M4 18v-2a4 4 0 013-3.87"
                  />
                </svg>
                Cast
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {seriesDetails.casts.map((cast) => (
                  <div
                    key={cast.castId}
                    className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
                  >
                    <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      {cast.profileImageUrl ? (
                        <img
                          src={resolveAssetUrl(cast.profileImageUrl)}
                          alt={cast.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <span className="text-xs font-semibold text-gray-600">
                          {cast.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 text-left">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {cast.name}
                      </p>
                      {cast.role && (
                        <p className="text-xs text-gray-500 truncate">{cast.role}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cast Assignment Modal */}
      {drama.dramaId && (
        <CastAssignmentModal
          isOpen={isCastModalOpen}
          onClose={() => setIsCastModalOpen(false)}
          contentType="drama"
          contentId={drama.dramaId}
          contentTitle={drama.title}
          fetchAssignedCasts={handleFetchAssignedCasts}
          searchCasts={handleSearchCasts}
          onAssign={async (castId, role) => {
            await assignCast(castId, role);
          }}
          onRemove={async (castId) => {
            await removeCast(castId);
          }}
          onUpdateRole={async (castId, role) => {
            await updateRole(castId, role);
          }}
        />
      )}
    </div>
  );
};

export default SeriesDetailPage;


